import { Button, Card, Icon } from '@zillow/constellation-10';
import { IconHomesFilled, IconSearchFilled } from '@zillow/constellation-icons';

import EmptyState from '../../../components/EmptyState';

type Props = {
  searchText?: string;
  includeArchived: boolean;
  onClearFilters: () => void;
  onShowArchived: () => void;
};

const getCopy = ({ searchText, includeArchived }: Pick<Props, 'searchText' | 'includeArchived'>) => {
  if (searchText && includeArchived) {
    return {
      icon: <IconSearchFilled />,
      heading: 'No properties found',
      body: `No archived properties match “${searchText}”. Try adjusting your search or clearing the archived filter.`,
      action: 'Clear filters',
    };
  }

  if (searchText) {
    return {
      icon: <IconSearchFilled />,
      heading: 'No properties found',
      body: `No properties match “${searchText}”. Try adjusting your search.`,
      action: 'Clear search',
    };
  }

  if (includeArchived) {
    return {
      icon: <IconHomesFilled />,
      heading: 'No archived properties',
      body: 'Properties you archive will show up here.',
      action: 'Clear filter',
    };
  }

  return {
    icon: <IconHomesFilled />,
    heading: 'No active properties',
    body: 'All of your properties are archived. Turn on the archived filter to see them.',
    action: 'Show archived',
  };
};

export const FlexiblePropertyNoResultsEmptyState = ({
  searchText,
  includeArchived,
  onClearFilters,
  onShowArchived,
}: Props) => {
  const copy = getCopy({ searchText, includeArchived });
  const hasActiveFilters = Boolean(searchText) || includeArchived;

  return (
    <Card
      tone="neutral"
      elevated={false}
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingInline: '400',
        paddingBlock: '400',
        width: '100%',
        minHeight: { xxl: '464px' },
      }}
    >
      <EmptyState
        useCard={false}
        align="center"
        data-testid="FlexiblePropertyNoResultsEmptyState"
        icon={<Icon render={copy.icon} size="md" css={{ color: 'icon.subtle' }} />}
        heading={copy.heading}
        headingLevel={3}
        body={copy.body}
        actions={
          <Button
            tone="brand"
            emphasis="outlined"
            onClick={hasActiveFilters ? onClearFilters : onShowArchived}
            data-testid="FlexiblePropertyNoResultsEmptyStateClearButton"
          >
            {copy.action}
          </Button>
        }
      />
    </Card>
  );
};
